// The one live call the demo makes to the ArcDisk backend: turning an asset
// id into a short-lived (15 min) URL for the student's actual file, so the
// raw submission can be shown before and after assessment. Everything else
// is bundled fixtures.
//
// Requires REACT_APP_ARCDISK_API_URL and, on the ArcDisk side, this origin in
// CORS_ALLOWED_ORIGINS (its proxy.ts already supports the allow-list). With
// no URL configured the hook reports an error and the viewer shows a quiet
// "preview unavailable" state instead of hanging on a skeleton.
import { useEffect, useState } from 'react'
import { ARCDISK_API_URL } from '../config'

export interface AssetUrl {
  asset_id: number
  url: string
  expires_in_s: number
}

const TTL_MS = 12 * 60 * 1000
const cache = new Map<number, { at: number; promise: Promise<AssetUrl> }>()

export function fetchAssetUrl(assetId: number): Promise<AssetUrl> {
  if (!ARCDISK_API_URL) return Promise.reject(new Error('REACT_APP_ARCDISK_API_URL is not set'))
  const hit = cache.get(assetId)
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise
  const promise = fetch(`${ARCDISK_API_URL}/api/media/${encodeURIComponent(assetId)}/url`, { cache: 'no-store' }).then(
    async r => {
      if (!r.ok) throw new Error(`asset ${assetId}: HTTP ${r.status}`)
      return (await r.json()) as AssetUrl
    },
  )
  promise.catch(() => cache.delete(assetId))
  cache.set(assetId, { at: Date.now(), promise })
  return promise
}

export function useAssetUrl(assetId: number | null | undefined) {
  const [state, setState] = useState<{ data: AssetUrl | null; isError: boolean; isLoading: boolean }>({
    data: null,
    isError: false,
    isLoading: Boolean(assetId),
  })
  useEffect(() => {
    if (!assetId) {
      setState({ data: null, isError: false, isLoading: false })
      return undefined
    }
    let alive = true
    setState({ data: null, isError: false, isLoading: true })
    fetchAssetUrl(assetId).then(
      data => alive && setState({ data, isError: false, isLoading: false }),
      () => alive && setState({ data: null, isError: true, isLoading: false }),
    )
    return () => {
      alive = false
    }
  }, [assetId])
  return state
}
